import { useMemo } from "react";
import { useRoomsUnread } from "./RoomsUnreadProvider";

/**
 * Resolve a FlexBoard user id to their coaching room, using rooms
 * `RoomsUnreadProvider` has already fetched. Costs no request of its own —
 * the provider lists every COACHING room on boot and on each resync tick, so
 * this is a lookup against data already in memory.
 *
 * Returns `null` when the client has no coaching room. Callers should render
 * no chat affordance at all in that case rather than an empty thread: there
 * is no client-side way to create a room, so an "open chat" button would be
 * a dead end.
 *
 * Note the index only covers rooms the viewer can see. A client coached by
 * someone else resolves to `null` here even though a room exists.
 *
 * @param {string|null|undefined} clientId
 * @returns {{roomId: string, unread: number, displayId: any,
 *   clientName: string, lastMessage: string|null, lastSeq: number}|null}
 */
export function useClientRoom(clientId) {
  const { clientRoomIndex, unreadByRoom } = useRoomsUnread();

  return useMemo(
    () => selectClientRoom(clientRoomIndex, unreadByRoom, clientId),
    [clientId, clientRoomIndex, unreadByRoom]
  );
}

/**
 * The non-hook form, for callers that resolve many clients at once (e.g. a
 * table column) and can't call a hook per row.
 *
 * @param {Map<string, Object>|undefined} clientRoomIndex
 * @param {Map<string, number>|undefined} unreadByRoom
 * @param {string|null|undefined} clientId
 * @returns {{roomId: string, unread: number, displayId: any,
 *   clientName: string, lastMessage: string|null, lastSeq: number}|null}
 */
export function selectClientRoom(clientRoomIndex, unreadByRoom, clientId) {
  if (!clientId) return null;
  const entry = clientRoomIndex?.get(String(clientId));
  if (!entry) return null;
  return {
    roomId: entry.roomId,
    displayId: entry.displayId,
    clientName: entry.clientName,
    lastMessage: entry.lastMessage || null,
    // Per-room message sequence. Monotonic within a room, so it orders
    // activity roughly across rooms — the key InboxList already sorts by.
    lastSeq: entry.lastSeq || 0,
    unread: unreadByRoom?.get(entry.roomId) || 0,
  };
}

export default useClientRoom;
